import LeaseRecordInput from 'src/components/leases/LeaseRecordInput';
import React, { useState, useEffect, useRef } from 'react';
import { ILeaseMaster, ILease1099 } from 'src/models/dlcLease/ILeaseMaster';
import * as styles from './styles';
import useFetch from 'use-http';
import { DateField, Field, SelectField } from './Master';
import Maintenance1099Modal from './Maintenance1099Modal';
import { Loader2 } from 'src/components/common/icons';
import { ILeaseTinNameAddress, isTinNameAddressMatch, normalizeTin, getNameAddress, LeaseNameAddress } from './tinNameAddressMatching';
import { applySend1099Selection, DEFAULT_SEND_1099_TYPE, getPrimaryInvoiceDueDate } from './lease1099Defaults';
import TinMismatchReviewDialog from './TinMismatchReviewDialog';

// ===================================================================================
// --- TYPE DEFINITIONS ---
// ===================================================================================

interface ICompany {
    id: number;
    name: string;
}

interface ILease1099Company {
    compId: number;
    companyName?: string;
}

interface ISendType {
    code: string;
    dscr: string;
}

interface ISend1099Types {
    send: ISendType[];
    noSend: ISendType[];
}

interface ILeaseTinLookup extends ILeaseTinNameAddress {
    tinId?: number;
}

type TinMatchStatus = 'loading' | 'match' | 'mismatch' | 'missing' | 'unknown';

type TinLookupStatus = 'loading' | 'found' | 'missing' | 'unknown';

interface ITinLookupCacheEntry {
    status: TinLookupStatus;
    tin?: ILeaseTinLookup;
}

interface ISection1099Props {
    leaseMaster: ILeaseMaster;
    onChange?: (field: string, value: ILease1099[] | undefined) => void;
    onSave?: () => Promise<boolean>;
    onNameAddressSaved?: (lsId: number, values: LeaseNameAddress) => void;
}

const EMPTY_1099_DATA: ILease1099[] = [];

interface ISend1099RadioFieldProps {
    name: string;
    value?: string;
    editable: boolean;
    onChange: (value: 'Y' | 'N') => void;
}

const Send1099RadioField: React.FC<ISend1099RadioFieldProps> = ({ name, value, editable, onChange }) => (
    <fieldset style={{ margin: 0, padding: 0, border: 0, minWidth: 0 }}>
        <legend style={{ marginBottom: '0.5rem', color: styles.colors.gray700, fontWeight: styles.typography.fontWeight.semibold }}>
            1099 Delivery
        </legend>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', minHeight: '2.375rem' }}>
            {[
                { value: 'Y' as const, label: 'Send 1099' },
                { value: 'N' as const, label: 'Do Not Send 1099' },
            ].map((option) => (
                <label key={option.value} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: editable ? 'pointer' : 'default' }}>
                    <LeaseRecordInput
                        type="radio"
                        name={name}
                        value={option.value}
                        checked={value === option.value}
                        disabled={!editable}
                        onChange={() => onChange(option.value)}
                    />
                    {option.label}
                </label>
            ))}
        </div>
    </fieldset>
);

// ===================================================================================
// --- MAIN COMPONENT: Section1099 ---
// ===================================================================================

const Section1099: React.FC<ISection1099Props> = ({ leaseMaster, onChange, onSave, onNameAddressSaved }) => {
    const [expanded1099Index, setExpanded1099Index] = useState<number | null>(null);
    const [showAddNew, setShowAddNew] = useState(false);
    const [new1099, setNew1099] = useState<Partial<ILease1099>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [companies, setCompanies] = useState<ICompany[]>([]);
    const [send1099Types, setSend1099Types] = useState<ISend1099Types>({ send: [], noSend: [] });
    const [isLoading1099Company, setIsLoading1099Company] = useState(false);
    const [companyLookupError, setCompanyLookupError] = useState<string | null>(null);

    // --- 1099 Maintenance Modal State ---
    const [isMaintModalOpen, setIsMaintModalOpen] = useState(false);
    const [selectedTinId, setSelectedTinId] = useState<number | null>(null);
    const [isLoadingTinIndex, setIsLoadingTinIndex] = useState<number | null>(null);
    const [tinReviewIndex, setTinReviewIndex] = useState<number | null>(null);
    const [tinReviewOriginal, setTinReviewOriginal] = useState<ILease1099 | null>(null);
    const tinLookupCacheRef = useRef<Record<string, ITinLookupCacheEntry>>({});
    const [tinLookupCache, setTinLookupCache] = useState<Record<string, ITinLookupCacheEntry>>({});

    const { get } = useFetch();
    const { put: putNameAddress, response: nameAddressResponse } = useFetch();

    const isEditable = !!onChange;
    const data1099 = leaseMaster.data1099 || EMPTY_1099_DATA;

    useEffect(() => {
        const fetchData = async (): Promise<void> => {
            try {
                const [companyResponse, typesResponse] = await Promise.all([get('/v1.0/leases/master/companies'), get('/v1.0/leases/master/1099s')]);

                if (companyResponse) {
                    setCompanies(companyResponse);
                }
                if (typesResponse) {
                    setSend1099Types(typesResponse);
                }
            } catch {
                // Keep the section usable if lookup data is unavailable.
            }
        };

        fetchData();
    }, [get]);

    // --- NEW LOGIC: Fetch the TIN ID based on SSN using find-by-tin ---
    const handleOpenMaintenance = async (ssn: string | undefined, index: number): Promise<void> => {
        if (!ssn || ssn.trim() === '') {
            alert('No SSN/Tax ID provided for this record.');
            return;
        }

        setIsLoadingTinIndex(index);

        const cleanSsn = normalizeTin(ssn);

        // Use the cleaned SSN in the API call
        const result = await get(`/v1.0/leases/tins/find-by-tin?tin=${encodeURIComponent(cleanSsn)}`);

        setIsLoadingTinIndex(null);

        // Since find-by-tin returns a single object (not an array), we check for result.tinId directly
        if (result && result.tinId) {
            setSelectedTinId(result.tinId);
            setIsMaintModalOpen(true);
        } else {
            alert('Could not locate a master TIN record for this SSN.');
        }
    };

    const getCompanyNameById = (compId?: number): string => {
        if (!compId) {
            return 'N/A';
        }
        const company = companies.find((c) => c.id === compId);
        return company ? company.name : 'Company Not Found';
    };

    const formatCurrency = (amount?: number): string => {
        if (amount === undefined || amount === null) {
            return '—';
        }
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
        }).format(amount);
    };

    const toCurrencyNumber = (amount?: number | null): number => {
        if (amount === undefined || amount === null || Number.isNaN(Number(amount))) {
            return 0;
        }

        return Number(amount);
    };

    const calculate1099AmountFromInvoices = (): number => {
        return (leaseMaster.invoices || [])
            .filter((invoice) => invoice.operation !== 'delete' && !invoice.voidDate)
            .reduce(
                (total, invoice) =>
                    total +
                    toCurrencyNumber(invoice.totBonus) +
                    toCurrencyNumber(invoice.advRental) +
                    toCurrencyNumber(invoice.other) +
                    toCurrencyNumber(invoice.addOther) +
                    toCurrencyNumber(invoice.other3),
                0
            );
    };

    const buildLessor1099Defaults = (): Partial<ILease1099> => {
        const lessor = leaseMaster.lessorDetails;

        return {
            ...new1099,
            name1: lessor?.name1 || new1099.name1,
            name2: lessor?.name2 || new1099.name2,
            addr1: lessor?.addr1 || new1099.addr1,
            addr2: lessor?.addr2 || new1099.addr2,
            cstz: lessor?.cstz || new1099.cstz,
            ssn: lessor?.ssn || new1099.ssn,
            amount: calculate1099AmountFromInvoices(),
            chkDate: new1099.chkDate || getPrimaryInvoiceDueDate(leaseMaster.invoices),
            send1099: new1099.send1099 || 'Y',
            sendType: new1099.send1099 === 'N' ? new1099.sendType : new1099.sendType || DEFAULT_SEND_1099_TYPE,
        };
    };

    const handlePullFromLessor = (): void => {
        setNew1099(buildLessor1099Defaults());
    };

    const handleOpenAdd1099 = async (): Promise<void> => {
        setNew1099(buildLessor1099Defaults());
        setShowAddNew(true);
        setCompanyLookupError(null);
        setIsLoading1099Company(true);

        try {
            const resolvedCompany = (await get(`/v1.0/leases/master/${leaseMaster.lsNo}/1099-company`)) as ILease1099Company;

            if (resolvedCompany && resolvedCompany.compId > 0) {
                setNew1099((current) => ({ ...current, compId: resolvedCompany.compId }));
                setCompanies((current) => {
                    if (current.some((company) => company.id === resolvedCompany.compId) || !resolvedCompany.companyName) {
                        return current;
                    }
                    return [...current, { id: resolvedCompany.compId, name: resolvedCompany.companyName }];
                });
            } else {
                setCompanyLookupError('The Check Company could not be determined. Please select one.');
            }
        } catch {
            setCompanyLookupError('The Check Company could not be determined. Please select one.');
        } finally {
            setIsLoading1099Company(false);
        }
    };

    const getW9StatusDisplay = (status?: string): { text: string; color: string } => {
        switch (status) {
            case 'Y':
                return { text: 'Verified', color: styles.colors.success };
            case 'N':
                return { text: 'Not Verified', color: styles.colors.danger };
            default:
                return { text: 'Pending', color: styles.colors.warning };
        }
    };

    const getTinMatchDisplay = (status?: TinMatchStatus): { text: string; color: string; backgroundColor: string } => {
        switch (status) {
            case 'match':
                return { text: 'YES', color: '#065f46', backgroundColor: '#d1fae5' };
            case 'mismatch':
                return { text: 'NO', color: '#ffffff', backgroundColor: styles.colors.danger };
            case 'missing':
                return { text: 'NO TIN RECORD', color: '#92400e', backgroundColor: '#fef3c7' };
            case 'loading':
                return { text: 'CHECKING', color: styles.colors.gray700, backgroundColor: styles.colors.gray200 };
            default:
                return { text: '-', color: styles.colors.gray600, backgroundColor: styles.colors.gray100 };
        }
    };

    const save1099Changes = async (): Promise<boolean> => {
        setIsSaving(true);
        const wasSaved = (await onSave?.()) ?? true;
        setIsSaving(false);
        return wasSaved;
    };

    const handle1099Change = (index: number, field: keyof ILease1099, value: unknown): void => {
        if (!onChange) {
            return;
        }
        const updatedData = [...data1099];
        const currentItem = updatedData[index];
        updatedData[index] = {
            ...currentItem,
            [field]: value,
            operation: currentItem.lsId ? 'update' : 'add',
        };
        onChange('data1099', updatedData);
    };

    const handleSend1099Change = (index: number, value: 'Y' | 'N'): void => {
        if (!onChange) {
            return;
        }
        const updatedData = [...data1099];
        const currentItem = updatedData[index];
        updatedData[index] = {
            ...applySend1099Selection(currentItem, value),
            operation: currentItem.lsId ? 'update' : 'add',
        };
        onChange('data1099', updatedData);
    };

    const handleAdd1099 = async (): Promise<void> => {
        if (!onChange) {
            return;
        }
        if (!new1099.compId) {
            alert('Please select a company before saving.');
            return;
        }
        if (!new1099.chkDate) {
            alert('Please enter a Check Date before saving. No active, non-voided invoice has a Payment Due Date to use as the default.');
            return;
        }

        const updatedData = [...data1099, { ...new1099, operation: 'add' } as ILease1099];
        onChange('data1099', updatedData);
        const wasSaved = await save1099Changes();
        if (wasSaved) {
            setNew1099({});
            setShowAddNew(false);
        }
    };

    const handleRemove1099 = async (index: number): Promise<void> => {
        if (!onChange || !confirm('Are you sure you want to remove this 1099 data?')) {
            return;
        }

        const item = data1099[index];
        const updatedData = [...data1099];
        if (item.lsId) {
            updatedData[index] = { ...item, operation: 'delete' };
        } else {
            updatedData.splice(index, 1);
        }

        onChange('data1099', updatedData);
        await save1099Changes();
    };

    const handleToggle1099 = async (index: number): Promise<void> => {
        if (expanded1099Index !== null) {
            const wasSaved = await save1099Changes();
            if (!wasSaved) {
                return;
            }
        }

        setExpanded1099Index(expanded1099Index === index ? null : index);
    };

    const visibleItems = data1099.map((item, dataIndex) => ({ item, dataIndex })).filter(({ item }) => item.operation !== 'delete');
    const activeTinSignature = Array.from(new Set(visibleItems.map(({ item }) => normalizeTin(item.ssn)).filter(Boolean)))
        .sort()
        .join('|');

    useEffect(() => {
        const activeTins = activeTinSignature ? activeTinSignature.split('|') : [];
        const tinsToFetch = activeTins.filter((tin) => !tinLookupCacheRef.current[tin]);

        if (tinsToFetch.length === 0) {
            // Explicit undefined: the other path returns a cleanup function, and
            // noImplicitReturns rejects a bare return alongside it.
            return undefined;
        }

        let isCancelled = false;

        setTinLookupCache((current) => {
            const next = { ...current };
            tinsToFetch.forEach((tin) => {
                next[tin] = { status: 'loading' };
            });
            tinLookupCacheRef.current = next;
            return next;
        });

        const loadTinLookups = async (): Promise<void> => {
            const lookupUpdates: Record<string, ITinLookupCacheEntry> = {};

            await Promise.all(
                tinsToFetch.map(async (tin) => {
                    try {
                        const result = (await get(`/v1.0/leases/tins/find-by-tin?tin=${encodeURIComponent(tin)}`)) as ILeaseTinLookup | undefined;
                        lookupUpdates[tin] = result?.tinId ? { status: 'found', tin: result } : { status: 'missing' };
                    } catch {
                        lookupUpdates[tin] = { status: 'unknown' };
                    }
                })
            );

            if (!isCancelled) {
                setTinLookupCache((current) => {
                    const next = { ...current, ...lookupUpdates };
                    tinLookupCacheRef.current = next;
                    return next;
                });
            }
        };

        loadTinLookups();

        return () => {
            isCancelled = true;
        };
    }, [activeTinSignature, get]);

    const getTinMatchStatus = (item: ILease1099): TinMatchStatus => {
        const cleanTin = normalizeTin(item.ssn);
        if (!cleanTin) {
            return 'unknown';
        }

        const lookup = tinLookupCache[cleanTin];
        if (!lookup || lookup.status === 'loading') {
            return 'loading';
        }
        if (lookup.status === 'missing') {
            return 'missing';
        }
        if (lookup.status === 'found' && lookup.tin) {
            return isTinNameAddressMatch(item, lookup.tin) ? 'match' : 'mismatch';
        }

        return 'unknown';
    };

    const refreshTinLookup = async (tin: string | undefined): Promise<void> => {
        const cleanTin = normalizeTin(tin);
        if (!cleanTin) {
            return;
        }

        setTinLookupCache((current) => {
            const next = { ...current, [cleanTin]: { status: 'loading' as const } };
            tinLookupCacheRef.current = next;
            return next;
        });

        try {
            const result = (await get(`/v1.0/leases/tins/find-by-tin?tin=${encodeURIComponent(cleanTin)}`)) as ILeaseTinLookup | undefined;
            setTinLookupCache((current) => {
                const next = { ...current, [cleanTin]: result?.tinId ? { status: 'found' as const, tin: result } : { status: 'missing' as const } };
                tinLookupCacheRef.current = next;
                return next;
            });
        } catch {
            setTinLookupCache((current) => {
                const next = { ...current, [cleanTin]: { status: 'unknown' as const } };
                tinLookupCacheRef.current = next;
                return next;
            });
        }
    };

    const handleTinReviewSave = async (index: number, draft: ILease1099): Promise<boolean> => {
        if (!onChange) {
            return false;
        }

        const currentItem = data1099[index];
        const previousTin = currentItem.ssn;
        const updatedData = [...data1099];
        updatedData[index] = { ...draft, operation: currentItem.lsId ? 'update' : 'add' };
        onChange('data1099', updatedData);

        const wasSaved = await save1099Changes();
        if (!wasSaved) {
            return false;
        }

        if (normalizeTin(previousTin) !== normalizeTin(draft.ssn)) {
            await refreshTinLookup(previousTin);
        }
        await refreshTinLookup(draft.ssn);
        return true;
    };

    const openTinReview = (index: number): void => {
        setTinReviewOriginal({ ...data1099[index] });
        setTinReviewIndex(index);
    };

    const closeTinReview = (discardFailedSave = false): void => {
        if (discardFailedSave && tinReviewIndex !== null && tinReviewOriginal && onChange) {
            const restoredData = [...data1099];
            restoredData[tinReviewIndex] = tinReviewOriginal;
            onChange('data1099', restoredData);
        }
        setTinReviewIndex(null);
        setTinReviewOriginal(null);
    };

    const tinReviewItem = tinReviewIndex === null ? null : data1099[tinReviewIndex];
    const tinReviewLookup = tinReviewItem ? tinLookupCache[normalizeTin(tinReviewItem.ssn)] : undefined;
    const tinReviewMaster = tinReviewLookup?.status === 'found' ? tinReviewLookup.tin : null;

    const updateTinComparison = async (destination: 'tin' | '1099'): Promise<void> => {
        if (!tinReviewMaster?.tinId || !tinReviewItem?.lsId || !onNameAddressSaved || !onChange) {
            throw new Error('Save the lease record and reload the comparison before updating.');
        }
        const source = destination === 'tin' ? tinReviewItem : tinReviewMaster;
        const path =
            destination === 'tin'
                ? `/v1.0/leases/tins/${tinReviewMaster.tinId}/name-address`
                : `/v1.0/leases/master/${leaseMaster.lsNo}/1099/${tinReviewItem.lsId}/name-address`;
        const result = await putNameAddress(path, getNameAddress(source));
        if (!nameAddressResponse.ok || !result || typeof result !== 'object' || !('name1' in result)) {
            throw new Error(result?.title || result?.message || 'The update could not be confirmed. Refresh the comparison and try again.');
        }
        const saved = getNameAddress(result);
        if (destination === '1099') {
            onNameAddressSaved(tinReviewItem.lsId, saved);
        } else {
            // Use the persisted values returned by the API, preserving the exact selected master.
            const tin = { ...tinReviewMaster, ...saved };
            const key = normalizeTin(tinReviewItem.ssn);
            setTinLookupCache((current) => {
                const next = { ...current, [key]: { status: 'found' as const, tin } };
                tinLookupCacheRef.current = next;
                return next;
            });
        }
    };

    return (
        <div style={styles.sectionContainer}>
            <h2 style={styles.sectionHeader}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={styles.colors.primary} strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <path d="M9 15h6" />
                    </svg>
                    1099 Tax Forms
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    {visibleItems.length > 0 && (
                        <span style={{ fontSize: styles.typography.fontSize.sm, color: styles.colors.gray600 }}>
                            {visibleItems.length} form{visibleItems.length !== 1 ? 's' : ''}
                        </span>
                    )}
                    {isEditable && (
                        <button
                            onClick={() => void handleOpenAdd1099()}
                            style={styles.buttonPrimary}
                            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                style={{ marginRight: '0.25rem' }}
                            >
                                <line x1="12" y1="5" x2="12" y2="19" />
                                <line x1="5" y1="12" x2="19" y2="12" />
                            </svg>
                            Add 1099
                        </button>
                    )}
                </div>
            </h2>

            {visibleItems.length === 0 && !showAddNew && (
                <div style={styles.emptyState}>
                    <svg
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={styles.colors.gray400}
                        strokeWidth="1.5"
                        style={{ margin: '0 auto 1rem' }}
                    >
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <path d="M12 18v-6" />
                        <path d="M9 15h6" />
                    </svg>
                    <p style={{ margin: 0, color: styles.colors.gray600, fontSize: styles.typography.fontSize.lg }}>No 1099 forms recorded</p>
                    {isEditable && (
                        <p style={{ marginTop: '0.5rem', fontSize: styles.typography.fontSize.sm, color: styles.colors.gray500 }}>
                            Click &quot;Add 1099&quot; to create your first tax form
                        </p>
                    )}
                </div>
            )}

            {visibleItems.map(({ item, dataIndex }) => {
                const verificationStatus = getW9StatusDisplay(item.w9Verify);
                const isExpanded = expanded1099Index === dataIndex;
                const isSend1099Checked = item.send1099 === 'Y';
                const isThisRowLoading = isLoadingTinIndex === dataIndex;
                const tinMatchStatus = getTinMatchStatus(item);
                const tinMatchDisplay = getTinMatchDisplay(tinMatchStatus);
                const irsTransmittedDisplay = item.transmitDate ? 'YES' : 'NO';

                return (
                    <div
                        key={item.lsId || dataIndex}
                        style={{
                            ...styles.cardBase,
                            marginBottom: '1rem',
                            border: `1px solid ${isExpanded ? styles.colors.borderFocus : styles.colors.border}`,
                            transition: 'all 0.2s ease',
                        }}
                    >
                        <div
                            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                            onClick={() => void handleToggle1099(dataIndex)}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                <div
                                    style={{
                                        width: '40px',
                                        height: '40px',
                                        borderRadius: '8px',
                                        backgroundColor: styles.colors.bgSecondary,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <span style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>1099</span>
                                </div>
                                <div>
                                    <div
                                        style={{
                                            fontWeight: styles.typography.fontWeight.semibold,
                                            color: styles.colors.gray900,
                                            fontSize: styles.typography.fontSize.base,
                                        }}
                                    >
                                        {[item.name1, item.name2].filter(Boolean).join(' ') || 'New 1099'}
                                    </div>
                                    <div
                                        style={{
                                            display: 'flex',
                                            gap: '1rem',
                                            marginTop: '0.25rem',
                                            fontSize: styles.typography.fontSize.sm,
                                            color: styles.colors.gray600,
                                        }}
                                    >
                                        <span>{formatCurrency(item.amount)}</span>
                                        {item.ssn && <span>SSN: •••-••-{item.ssn.slice(-4)}</span>}
                                    </div>
                                </div>
                                <div style={{ fontWeight: 'bold', color: styles.colors.gray900, marginLeft: '8px' }}>
                                    {getCompanyNameById(item.compId)}
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                                {/* --- UPDATED: 1099 Maintenance Button --- */}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenMaintenance(item.ssn, dataIndex);
                                    }}
                                    disabled={isThisRowLoading}
                                    style={{
                                        ...styles.buttonSecondary,
                                        padding: '0.25rem 0.75rem',
                                        fontSize: styles.typography.fontSize.sm,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.35rem',
                                        opacity: isThisRowLoading ? 0.7 : 1,
                                        cursor: isThisRowLoading ? 'not-allowed' : 'pointer',
                                    }}
                                >
                                    {isThisRowLoading ? (
                                        <Loader2 className="animate-spin" style={{ width: '14px', height: '14px' }} />
                                    ) : (
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                            <polyline points="14 2 14 8 20 8" />
                                            <line x1="16" y1="13" x2="8" y2="13" />
                                        </svg>
                                    )}
                                    {isThisRowLoading ? 'Loading...' : '1099 Maintenance'}
                                </button>

                                <span
                                    style={{
                                        ...styles.badge,
                                        backgroundColor: verificationStatus.color + '20',
                                        color: verificationStatus.color,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.25rem',
                                    }}
                                >
                                    W9 {verificationStatus.text}
                                </span>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openTinReview(dataIndex);
                                    }}
                                    disabled={tinMatchStatus === 'loading' || tinMatchStatus === 'unknown'}
                                    style={{
                                        ...styles.badge,
                                        backgroundColor: tinMatchDisplay.backgroundColor,
                                        color: tinMatchDisplay.color,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.25rem',
                                        border: 0,
                                        cursor: tinMatchStatus === 'loading' || tinMatchStatus === 'unknown' ? 'default' : 'pointer',
                                    }}
                                    title={
                                        tinMatchStatus === 'mismatch' || tinMatchStatus === 'missing'
                                            ? 'Review and correct TIN match fields'
                                            : 'Review TIN match fields'
                                    }
                                >
                                    Matches TIN Name/Address: {tinMatchDisplay.text}
                                </button>
                                {isEditable && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            void handleRemove1099(dataIndex);
                                        }}
                                        style={{
                                            ...styles.buttonDanger,
                                            padding: '0.375rem 0.75rem',
                                            fontSize: styles.typography.fontSize.sm,
                                        }}
                                        onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                                        onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                                    >
                                        Remove
                                    </button>
                                )}
                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke={styles.colors.gray400}
                                    strokeWidth="2"
                                    style={{
                                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                        transition: 'transform 0.2s ease',
                                    }}
                                >
                                    <polyline points="6 9 12 15 18 9" />
                                </svg>
                            </div>
                        </div>

                        {isExpanded && (
                            <div
                                style={{
                                    marginTop: '1.5rem',
                                    paddingTop: '1.5rem',
                                    borderTop: `1px solid ${styles.colors.border}`,
                                }}
                            >
                                <div
                                    style={{
                                        marginBottom: '2rem',
                                        padding: '1.5rem',
                                        backgroundColor: styles.colors.gray50,
                                        borderRadius: '8px',
                                        border: `1px solid ${styles.colors.gray200}`,
                                    }}
                                >
                                    <h3
                                        style={{
                                            margin: '0 0 1rem 0',
                                            fontSize: styles.typography.fontSize.lg,
                                            fontWeight: styles.typography.fontWeight.semibold,
                                            color: styles.colors.gray800,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                        }}
                                    >
                                        Personal Information
                                    </h3>
                                    <div style={styles.gridCols3}>
                                        <SelectField
                                            label="Company"
                                            value={item.compId?.toString() || ''}
                                            editable={isEditable}
                                            onChange={(value) => handle1099Change(dataIndex, 'compId', value ? parseInt(value, 10) : undefined)}
                                            options={[
                                                { value: '', label: 'Select Company...' },
                                                ...companies.map((c) => ({
                                                    value: c.id.toString(),
                                                    label: c.name,
                                                })),
                                            ]}
                                        />
                                        <Field
                                            label="Name"
                                            value={item.name1 || ''}
                                            editable={isEditable}
                                            onChange={(value) => handle1099Change(dataIndex, 'name1', value)}
                                        />
                                        <Field
                                            label="Name 2"
                                            value={item.name2 || ''}
                                            editable={isEditable}
                                            onChange={(value) => handle1099Change(dataIndex, 'name2', value)}
                                        />
                                        <Field
                                            label="SSN/Tax ID"
                                            value={item.ssn || ''}
                                            editable={isEditable}
                                            onChange={(value) => handle1099Change(dataIndex, 'ssn', value)}
                                        />
                                        <Field
                                            label="Matches TIN Name/Address"
                                            value={tinMatchDisplay.text}
                                            editable={false}
                                            inputStyle={{
                                                backgroundColor: tinMatchDisplay.backgroundColor,
                                                color: tinMatchDisplay.color,
                                                borderColor: tinMatchDisplay.backgroundColor,
                                                fontWeight: styles.typography.fontWeight.semibold,
                                            }}
                                        />
                                    </div>
                                </div>

                                <div
                                    style={{
                                        marginBottom: '2rem',
                                        padding: '1.5rem',
                                        backgroundColor: styles.colors.gray50,
                                        borderRadius: '8px',
                                        border: `1px solid ${styles.colors.gray200}`,
                                    }}
                                >
                                    <h3
                                        style={{
                                            margin: '0 0 1rem 0',
                                            fontSize: styles.typography.fontSize.lg,
                                            fontWeight: styles.typography.fontWeight.semibold,
                                            color: styles.colors.gray800,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                        }}
                                    >
                                        Address
                                    </h3>
                                    <div style={styles.gridCols2}>
                                        <Field
                                            label="Address 1"
                                            value={item.addr1 || ''}
                                            editable={isEditable}
                                            onChange={(value) => handle1099Change(dataIndex, 'addr1', value)}
                                        />
                                        <Field
                                            label="Address 2"
                                            value={item.addr2 || ''}
                                            editable={isEditable}
                                            onChange={(value) => handle1099Change(dataIndex, 'addr2', value)}
                                        />
                                        <div style={{ gridColumn: '1 / -1' }}>
                                            <Field
                                                label="City/State/Zip"
                                                value={item.cstz || ''}
                                                editable={isEditable}
                                                onChange={(value) => handle1099Change(dataIndex, 'cstz', value)}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div
                                    style={{
                                        marginBottom: '2rem',
                                        padding: '1.5rem',
                                        backgroundColor: styles.colors.gray50,
                                        borderRadius: '8px',
                                        border: `1px solid ${styles.colors.gray200}`,
                                    }}
                                >
                                    <h3
                                        style={{
                                            margin: '0 0 1rem 0',
                                            fontSize: styles.typography.fontSize.lg,
                                            fontWeight: styles.typography.fontWeight.semibold,
                                            color: styles.colors.gray800,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                        }}
                                    >
                                        Financial Details
                                    </h3>
                                    <div style={styles.gridCols3}>
                                        <Field
                                            label="Amount"
                                            value={item.amount?.toString() || ''}
                                            type="number"
                                            prefix="$"
                                            editable={isEditable}
                                            onChange={(value) => handle1099Change(dataIndex, 'amount', value ? parseFloat(value) : undefined)}
                                        />
                                        <Field
                                            label="IRS Transmitted"
                                            value={irsTransmittedDisplay}
                                            editable={false}
                                            inputStyle={{
                                                backgroundColor: item.transmitDate ? '#d1fae5' : styles.colors.gray100,
                                                color: item.transmitDate ? '#065f46' : styles.colors.gray600,
                                                fontWeight: styles.typography.fontWeight.semibold,
                                            }}
                                        />
                                        <DateField
                                            label="Transmit Date"
                                            value={item.transmitDate}
                                            editable={isEditable}
                                            onChange={(value) => handle1099Change(dataIndex, 'transmitDate', value)}
                                        />
                                        <DateField
                                            label="Check Date"
                                            value={item.chkDate}
                                            editable={isEditable}
                                            onChange={(value) => handle1099Change(dataIndex, 'chkDate', value)}
                                        />
                                        <Send1099RadioField
                                            name={`send-1099-${item.lsId || dataIndex}`}
                                            value={item.send1099}
                                            editable={isEditable}
                                            onChange={(value) => handleSend1099Change(dataIndex, value)}
                                        />
                                        <SelectField
                                            label={isSend1099Checked ? 'Send 1099 Type' : 'No Send 1099 Type'}
                                            value={isSend1099Checked ? item.sendType || '' : item.noSendType || ''}
                                            editable={isEditable}
                                            onChange={(value) => handle1099Change(dataIndex, isSend1099Checked ? 'sendType' : 'noSendType', value)}
                                            options={
                                                isSend1099Checked
                                                    ? [
                                                          { value: '', label: 'Select...' },
                                                          ...send1099Types.send.map((t) => ({ value: t.code, label: t.dscr })),
                                                      ]
                                                    : [
                                                          { value: '', label: 'Select...' },
                                                          ...send1099Types.noSend.map((t) => ({ value: t.code, label: t.dscr })),
                                                      ]
                                            }
                                        />

                                        {!isSend1099Checked && item.noSendType === 'OT' ? (
                                            <Field
                                                label="Reason"
                                                value={item.noSendReason || ''}
                                                editable={isEditable}
                                                onChange={(value) => handle1099Change(dataIndex, 'noSendReason', value)}
                                            />
                                        ) : (
                                            <div />
                                        )}

                                        <Field
                                            label="Withholding Amount"
                                            value={item.withAmount?.toString() || ''}
                                            type="number"
                                            prefix="$"
                                            editable={isEditable}
                                            onChange={(value) => handle1099Change(dataIndex, 'withAmount', value ? parseFloat(value) : undefined)}
                                        />
                                        <Field
                                            label="Correct Amount"
                                            value={item.correctAmt?.toString() || ''}
                                            type="number"
                                            prefix="$"
                                            editable={isEditable}
                                            onChange={(value) => handle1099Change(dataIndex, 'correctAmt', value ? parseFloat(value) : undefined)}
                                        />
                                        <DateField
                                            label="Correct Date"
                                            value={item.correctDate}
                                            editable={isEditable}
                                            onChange={(value) => handle1099Change(dataIndex, 'correctDate', value)}
                                        />
                                        <DateField
                                            label="Correct Transmit Date"
                                            value={item.correctTransDate}
                                            editable={isEditable}
                                            onChange={(value) => handle1099Change(dataIndex, 'correctTransDate', value)}
                                        />
                                    </div>
                                </div>

                                <div
                                    style={{
                                        marginBottom: '2rem',
                                        padding: '1.5rem',
                                        backgroundColor: styles.colors.gray50,
                                        borderRadius: '8px',
                                        border: `1px solid ${styles.colors.gray200}`,
                                    }}
                                >
                                    <h3
                                        style={{
                                            margin: '0 0 1rem 0',
                                            fontSize: styles.typography.fontSize.lg,
                                            fontWeight: styles.typography.fontWeight.semibold,
                                            color: styles.colors.gray800,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                        }}
                                    >
                                        W9 Verification
                                    </h3>
                                    <div style={styles.gridCols3}>
                                        <SelectField
                                            label="W9 Verified"
                                            value={item.w9Verify || ''}
                                            editable={isEditable}
                                            onChange={(value) => handle1099Change(dataIndex, 'w9Verify', value)}
                                            options={[
                                                { value: '', label: 'Select...' },
                                                { value: 'Y', label: 'Yes' },
                                                { value: 'N', label: 'No' },
                                            ]}
                                        />
                                        <DateField
                                            label="W9 Verify Date"
                                            value={item.w9VerifyDate}
                                            editable={isEditable}
                                            onChange={(value) => handle1099Change(dataIndex, 'w9VerifyDate', value)}
                                        />
                                        <Field
                                            label="W9 Verified By"
                                            value={item.w9VerifyBy || ''}
                                            editable={isEditable}
                                            onChange={(value) => handle1099Change(dataIndex, 'w9VerifyBy', value)}
                                        />
                                    </div>
                                </div>

                                <div
                                    style={{
                                        padding: '1.5rem',
                                        backgroundColor: styles.colors.gray50,
                                        borderRadius: '8px',
                                        border: `1px solid ${styles.colors.gray200}`,
                                    }}
                                >
                                    <h3
                                        style={{
                                            margin: '0 0 1rem 0',
                                            fontSize: styles.typography.fontSize.lg,
                                            fontWeight: styles.typography.fontWeight.semibold,
                                            color: styles.colors.gray800,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                        }}
                                    >
                                        Additional Information
                                    </h3>
                                    <div style={{ display: 'grid', gap: '1rem' }}>
                                        <Field
                                            label="Remarks"
                                            value={item.remarkNotes || ''}
                                            editable={isEditable}
                                            onChange={(value) => handle1099Change(dataIndex, 'remarkNotes', value)}
                                            type="textarea"
                                        />
                                    </div>
                                </div>

                                {isEditable && (
                                    <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
                                        <button
                                            onClick={() => void handleToggle1099(dataIndex)}
                                            disabled={isSaving}
                                            style={{ ...styles.buttonSuccess, opacity: isSaving ? 0.7 : 1 }}
                                        >
                                            {isSaving ? 'Saving...' : 'Save 1099'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}

            {showAddNew && isEditable && (
                <div
                    style={{
                        ...styles.cardBase,
                        backgroundColor: styles.colors.bgSecondary,
                        border: `2px dashed ${styles.colors.primary}`,
                        marginTop: '1rem',
                    }}
                >
                    <div
                        style={{
                            marginBottom: '1.5rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '1rem',
                        }}
                    >
                        <h3
                            style={{
                                margin: 0,
                                fontSize: styles.typography.fontSize.xl,
                                color: styles.colors.gray900,
                                fontWeight: styles.typography.fontWeight.semibold,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.75rem',
                            }}
                        >
                            Add New 1099
                        </h3>
                        <button
                            onClick={handlePullFromLessor}
                            style={styles.buttonSecondary}
                            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                        >
                            Pull from Lessor
                        </button>
                    </div>
                    <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(2, 1fr)' }}>
                        <div>
                            <SelectField
                                label="Company (Required)"
                                editable={isEditable && !isLoading1099Company}
                                value={new1099.compId?.toString() || ''}
                                onChange={(value) => setNew1099({ ...new1099, compId: value ? parseInt(value, 10) : undefined })}
                                options={[
                                    { value: '', label: isLoading1099Company ? 'Determining Check Company...' : 'Select a Company...' },
                                    ...companies.map((c) => ({ value: c.id.toString(), label: c.name })),
                                ]}
                            />
                            {companyLookupError ? <p style={{ margin: '0.35rem 0 0', color: styles.colors.danger }}>{companyLookupError}</p> : null}
                        </div>
                        <Field label="Name" value={new1099.name1 || ''} onChange={(value) => setNew1099({ ...new1099, name1: value })} />
                        <Field label="Name 2" value={new1099.name2 || ''} onChange={(value) => setNew1099({ ...new1099, name2: value })} />
                        <Field
                            label="Amount"
                            value={new1099.amount?.toString() || ''}
                            type="number"
                            prefix="$"
                            onChange={(value) => setNew1099({ ...new1099, amount: value ? parseFloat(value) : undefined })}
                        />
                        <div>
                            <DateField
                                label="Check Date (Required)"
                                value={new1099.chkDate}
                                editable={isEditable}
                                onChange={(value) => setNew1099({ ...new1099, chkDate: value || undefined })}
                                helpText="Defaults from the first active, non-voided invoice with a Payment Due Date. Change it when this 1099 applies to a different payment."
                            />
                        </div>
                        <Send1099RadioField
                            name="new-send-1099"
                            value={new1099.send1099}
                            editable={isEditable}
                            onChange={(value) => setNew1099((current) => applySend1099Selection(current, value))}
                        />
                        <SelectField
                            label={new1099.send1099 === 'N' ? 'No Send 1099 Type' : 'Send 1099 Type'}
                            value={new1099.send1099 === 'N' ? new1099.noSendType || '' : new1099.sendType || ''}
                            editable={isEditable}
                            onChange={(value) =>
                                setNew1099({
                                    ...new1099,
                                    [new1099.send1099 === 'N' ? 'noSendType' : 'sendType']: value,
                                })
                            }
                            options={
                                new1099.send1099 === 'N'
                                    ? [{ value: '', label: 'Select...' }, ...send1099Types.noSend.map((t) => ({ value: t.code, label: t.dscr }))]
                                    : [{ value: '', label: 'Select...' }, ...send1099Types.send.map((t) => ({ value: t.code, label: t.dscr }))]
                            }
                        />
                        {new1099.send1099 === 'N' && new1099.noSendType === 'OT' ? (
                            <Field
                                label="Reason"
                                value={new1099.noSendReason || ''}
                                onChange={(value) => setNew1099({ ...new1099, noSendReason: value })}
                            />
                        ) : null}
                        <Field label="SSN/Tax ID" value={new1099.ssn || ''} onChange={(value) => setNew1099({ ...new1099, ssn: value })} />
                        <Field label="Address 1" value={new1099.addr1 || ''} onChange={(value) => setNew1099({ ...new1099, addr1: value })} />
                        <Field label="Address 2" value={new1099.addr2 || ''} onChange={(value) => setNew1099({ ...new1099, addr2: value })} />
                        <div style={{ gridColumn: '1 / -1' }}>
                            <Field label="City/State/Zip" value={new1099.cstz || ''} onChange={(value) => setNew1099({ ...new1099, cstz: value })} />
                        </div>
                    </div>
                    <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.75rem' }}>
                        <button
                            onClick={() => void handleAdd1099()}
                            disabled={isSaving}
                            style={styles.buttonSuccess}
                            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                style={{ marginRight: '0.25rem' }}
                            >
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                            {isSaving ? 'Saving...' : 'Save 1099'}
                        </button>
                        <button
                            onClick={() => {
                                setShowAddNew(false);
                                setNew1099({});
                            }}
                            style={styles.buttonSecondary}
                            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
                            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {tinReviewIndex !== null && tinReviewItem && (
                <TinMismatchReviewDialog
                    isOpen={true}
                    lease1099={tinReviewItem}
                    masterTin={tinReviewMaster}
                    isSaving={isSaving}
                    onClose={closeTinReview}
                    onSave={(draft) => handleTinReviewSave(tinReviewIndex, draft)}
                    onUpdate={onChange && onNameAddressSaved ? updateTinComparison : undefined}
                />
            )}

            {/* --- 1099 Maintenance Modal Render --- */}
            {isMaintModalOpen && selectedTinId !== null && (
                <Maintenance1099Modal isOpen={isMaintModalOpen} onClose={() => setIsMaintModalOpen(false)} tinId={selectedTinId} />
            )}
        </div>
    );
};

export default Section1099;



// WEBPACK FOOTER //
// ./src/components/leases/sections/1099.tsx